// Header / value normalization shared by column auto-detect and name resolution.
export const normalize = (s) => (s ?? '').toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '');

// Given the file's headers and a field list (each with `key` and `aliases`), guess which
// column maps to which field. Returns { [colIndex]: fieldKey }. Each field is claimed by at
// most one column; each column maps to at most one field. First match wins.
export function autoDetectMapping(headers, fields) {
  const mapping = {};
  const claimedFields = new Set();
  headers.forEach((header, colIndex) => {
    const normHeader = normalize(header);
    if (!normHeader) return;
    const match = fields.find(f => !claimedFields.has(f.key) && f.aliases.some(a => normalize(a) === normHeader));
    if (match) {
      mapping[colIndex] = match.key;
      claimedFields.add(match.key);
    }
  });
  return mapping;
}

// Levenshtein distance, used only to suggest a likely match when an exact name lookup fails.
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
}

// Exact (normalized) match by name against a list of {id, name} candidates. On no match,
// includes the closest candidate as a "did you mean" suggestion if it's a plausibly close typo.
// If more than one candidate shares the same normalized name, this returns ambiguous:true
// rather than silently picking one — misattributing a match by name is worse than an error.
export function findByName(rawName, candidates, nameKey = 'name') {
  const target = normalize(rawName);
  if (!target) return { match: null, suggestion: null, ambiguous: false };
  const exactMatches = candidates.filter(c => normalize(c[nameKey]) === target);
  if (exactMatches.length > 1) return { match: null, suggestion: null, ambiguous: true };
  if (exactMatches.length === 1) return { match: exactMatches[0], suggestion: null, ambiguous: false };

  let best = null, bestDist = Infinity;
  for (const c of candidates) {
    const d = levenshtein(target, normalize(c[nameKey]));
    if (d < bestDist) { bestDist = d; best = c; }
  }
  const suggestion = best && bestDist <= Math.max(2, Math.floor(target.length * 0.25)) ? best[nameKey] : null;
  return { match: null, suggestion, ambiguous: false };
}
